import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, DollarSign, FileText, Mail, LogOut, Calendar, Image, UserCheck, Newspaper, Bell, KeyRound, BarChart3, Tag, MapPin, Send, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { DriveGoalSettings } from '../components/admin/DriveGoalSettings';

const AdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        if (!token) { navigate('/admin/login'); return; }
        const response = await fetch('/api/admin/dashboard', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          if (response.status === 401) { localStorage.removeItem('admin_token'); navigate('/admin/login'); }
          throw new Error('Failed');
        }
        setStats(await response.json());
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
        navigate('/admin/login');
      } finally { setLoading(false); }
    };
    fetchDashboardStats();
  }, [navigate]);

  const sendDigest = async () => {
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/digest/send`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } });
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.sent) toast.success(`Digest emailed to info@ndpbd.com (${d.pending} pending)`);
    else toast.message(d.reason ? `Not sent: ${d.reason}` : 'Digest failed');
  };

  const handleLogout = () => { localStorage.removeItem('admin_token'); navigate('/'); };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-party-red mx-auto"></div>
      </div>
    );
  }

  const statCards = [
    { label: { en: 'Total Members', bn: 'মোট সদস্য' }, value: stats?.total_members || 0, sub: `${stats?.pending_members || 0} pending`, icon: Users, color: 'text-blue-600 bg-blue-50' },
    { label: { en: 'Donations', bn: 'দান' }, value: `${(stats?.total_donation_amount || 0).toFixed(0)} BDT`, sub: `${stats?.total_donations || 0} donors`, icon: DollarSign, color: 'text-green-600 bg-green-50' },
    { label: { en: 'Messages', bn: 'বার্তা' }, value: stats?.unread_contacts || 0, sub: 'unread', icon: Mail, color: 'text-orange-600 bg-orange-50' },
    { label: { en: 'News', bn: 'সংবাদ' }, value: stats?.total_news || 0, sub: 'articles', icon: Newspaper, color: 'text-purple-600 bg-purple-50' },
    { label: { en: 'Events', bn: 'ইভেন্ট' }, value: stats?.total_events || 0, sub: 'scheduled', icon: Calendar, color: 'text-party-red bg-party-red-100' },
    { label: { en: 'Leaders', bn: 'নেতা' }, value: stats?.total_leaders || 0, sub: 'profiles', icon: UserCheck, color: 'text-party-green bg-party-green-100' },
    { label: { en: 'Gallery', bn: 'গ্যালারি' }, value: stats?.total_gallery || 0, sub: 'images', icon: Image, color: 'text-pink-600 bg-pink-50' },
  ];

  const quickActions = [
    { label: { en: 'Manage Members', bn: 'সদস্য পরিচালনা' }, path: '/admin/members', icon: Users, color: 'bg-blue-600 hover:bg-blue-700' },
    { label: { en: 'Manage News', bn: 'সংবাদ পরিচালনা' }, path: '/admin/news', icon: Newspaper, color: 'bg-purple-600 hover:bg-purple-700' },
    { label: { en: 'Manage Events', bn: 'ইভেন্ট পরিচালনা' }, path: '/admin/events', icon: Calendar, color: 'bg-party-red hover:bg-party-red-600' },
    { label: { en: 'Manage Leaders', bn: 'নেতা পরিচালনা' }, path: '/admin/leaders', icon: UserCheck, color: 'bg-party-green hover:bg-party-green-600' },
    { label: { en: 'Manage Gallery', bn: 'গ্যালারি পরিচালনা' }, path: '/admin/gallery', icon: Image, color: 'bg-pink-600 hover:bg-pink-700' },
    { label: { en: 'Change Password', bn: 'পাসওয়ার্ড পরিবর্তন' }, path: '/admin/leaders', icon: KeyRound, color: 'bg-gray-700 hover:bg-gray-800' },
    { label: { en: 'Media Analytics', bn: 'মিডিয়া বিশ্লেষণ' }, path: '/admin/media-analytics', icon: BarChart3, color: 'bg-teal-600 hover:bg-teal-700' },
    { label: { en: 'Partner Deals', bn: 'পার্টনার অফার' }, path: '/admin/partners', icon: Tag, color: 'bg-amber-600 hover:bg-amber-700' },
    { label: { en: 'District Chapters', bn: 'জেলা শাখা' }, path: '/admin/districts', icon: MapPin, color: 'bg-emerald-700 hover:bg-emerald-800' },
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t({ en: 'Admin Dashboard', bn: 'অ্যাডমিন ড্যাশবোর্ড' })}</h1>
            <p className="text-sm text-gray-500">{t({ en: 'NDP Bangladesh Management', bn: 'এনডিপি বাংলাদেশ ব্যবস্থাপনা' })}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="view-site-btn">
              {t({ en: 'View Site', bn: 'সাইট দেখুন' })}
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" />{t({ en: 'Logout', bn: 'লগআউট' })}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats?.pending_members > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4" data-testid="pending-approval-alert">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
            <p className="flex-1 text-sm text-amber-900"><b>{stats.pending_members}</b> {t({ en: 'members are waiting for approval. Approving them unlocks partner discounts on their ID card.', bn: 'জন সদস্য অনুমোদনের অপেক্ষায়। অনুমোদন দিলে তাদের আইডি কার্ডে পার্টনার ছাড় চালু হবে।' })}</p>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/admin/members')} data-testid="review-pending-btn">{t({ en: 'Review now', bn: 'এখনই দেখুন' })}</Button>
            <Button size="sm" variant="outline" onClick={sendDigest} data-testid="send-digest-btn"><Send className="mr-1 h-4 w-4" /> {t({ en: 'Email digest now', bn: 'এখনই ডাইজেস্ট পাঠান' })}</Button>
            <p className="w-full text-xs text-amber-700">{t({ en: 'An approval digest is emailed to info@ndpbd.com automatically once a day at 9:00 AM (Dhaka).', bn: 'প্রতিদিন সকাল ৯টায় (ঢাকা) info@ndpbd.com-এ স্বয়ংক্রিয়ভাবে ডাইজেস্ট পাঠানো হয়।' })}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {statCards.map((s, i) => (
            <Card key={i} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4 text-center">
                <div className={`w-10 h-10 rounded-full ${s.color} flex items-center justify-center mx-auto mb-2`}>
                  <s.icon className="h-5 w-5" />
                </div>
                <div className="text-2xl font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500">{t(s.label)}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <DriveGoalSettings />

        {/* Quick Actions */}
        <Card className="mb-8">
          <CardHeader><CardTitle>{t({ en: 'Content Management', bn: 'কন্টেন্ট ব্যবস্থাপনা' })}</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {quickActions.map((action, i) => (
                <Button key={i} className={`${action.color} text-white h-auto py-4 flex flex-col gap-2`} onClick={() => navigate(action.path)} data-testid={`action-${action.path.split('/').pop()}`}>
                  <action.icon className="h-6 w-6" />
                  <span className="text-sm">{t(action.label)}</span>
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
